import React from 'react';
import { Trans } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import moment from 'moment';
import Page from 'flow/components/Setting/Page';
import SettingSection from 'flow/components/Setting/SettingSection';
import Button from 'flow/components/ui-components/Button';
import Divider from 'flow/components/ui-components/Divider';
import Loading from 'flow/components/ui-components/Loading';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppSelector } from 'flow/hooks/useRedux';
import { selectTemporaryPasswordState } from 'flow/selectors/app-selectors';
import { selectTokens } from 'flow/selectors/integration-access-selectors';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { routes } from 'flow/types/routes';
import { generatePath } from 'flow/utils/router-utils';

const ManageTokens = () => {
    const { i18next } = useAppContext();
    const history = useHistory();
    const { openModal } = useModal();
    const { extensionKey } = useParams<{ extensionKey: ExtensionKey }>();

    const accessTokens = useAppSelector(selectTokens);
    const usedTempPassword = useAppSelector(selectTemporaryPasswordState);

    if (!accessTokens) {
        return <Loading />;
    }

    const filteredTokens = accessTokens
        .filter(
            (token) =>
                token.get('invalidatedDate') === undefined ||
                moment(token.get('invalidatedDate')).isAfter(moment()),
        )
        .sortBy((token) => token.get('key'));

    return (
        <Page
            header="settings.integrations.manage_tokens_for_integration"
            onClickBack={() =>
                history.push(
                    generatePath(routes['settings.account.username-password'], {
                        extensionKey,
                    }),
                )
            }
            action={
                usedTempPassword ? null : (
                    <Button
                        color="primary"
                        onClick={() =>
                            openModal('createToken', { extensionKey })
                        }
                        iconKey="add"
                        className="!min-w-0 py-1"
                        hideTextOnOnePanel={true}
                    >
                        {i18next.t('settings.integrations.create_new_token')}
                    </Button>
                )
            }
        >
            <p>
                <Trans
                    i18n={i18next}
                    i18nKey="settings.integrations.use_integration_tokens"
                    components={[
                        <span key="0" className="font-bold text-black" />,
                    ]}
                />
                &nbsp;
                {i18next.t('settings.integrations.separate_tokens')}
                &nbsp;
                {i18next.t('settings.integrations.your_tokens_are_personal')}
            </p>

            <Divider />

            <SettingSection>
                {i18next.t('settings.integrations.your_tokens')}
            </SettingSection>

            {filteredTokens.isEmpty() ? (
                <span className="p-2">
                    {i18next.t('settings.integrations.no_tokens')}
                </span>
            ) : (
                filteredTokens
                    .map((token) => (
                        <div
                            key={token.get('key')}
                            className="flex items-center justify-between p-2"
                        >
                            <div>{token.get('domainName')}</div>
                            <div className="flex items-center gap-2 text-sm">
                                <span className="text-gray-500">
                                    {token.get('description')}
                                </span>
                                <Button
                                    className="text-red !min-w-0"
                                    onClick={() =>
                                        openModal('revokeToken', {
                                            id: token.get('key'),
                                            extensionKey,
                                        })
                                    }
                                >
                                    {i18next.t('revoke')}
                                </Button>
                            </div>
                        </div>
                    ))
                    .toArray()
            )}

            <Divider />
        </Page>
    );
};

export default ManageTokens;
